import React, { useState, useCallback } from 'react'
import type { CustomTextInputProps } from './index.type'
import { useTranslation } from 'react-i18next'

export default React.memo<CustomTextInputProps>(function CustomTextInput(props) {
  const { t } = useTranslation()
  
  const {
    value,
    onChangeText,
    onBlur: onBlurProp,
    onFocus: onFocusProp,
    secureTextEntry,
    isShowSmallEyes = false,
    className = '',
    isErrored = false,
    size = 'md',
    // 边框相关属性
    showBorder = true,
    borderColor,
    borderWidth,
    borderRadius,
    // 背景色相关属性
    backgroundColor,
    // 禁用状态
    disabled = false,
    // 剩余的原生 input 属性
    ...restProps
  } = props

  const [isFocus, setIsFocus] = useState(false)
  const [isSecureTextEntry, setIsSecureTextEntry] = useState(Boolean(secureTextEntry))

  const handleEyesIconPress = useCallback(() => {
    setIsSecureTextEntry(prev => !prev)
  }, [])

  const handleFocus = useCallback(() => {
    setIsFocus(true)
    onFocusProp?.()
  }, [onFocusProp])

  const handleBlur = useCallback(() => {
    setIsFocus(false)
    onBlurProp?.()
  }, [onBlurProp])

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    onChangeText?.(e.target.value)
  }, [onChangeText])

  // 根据 size 获取对应的 UnoCSS 类名
  const getSizeClasses = () => {
    switch (size) {
      case 'sm':
        return 'h-10 text-sm px-3'
      case 'lg':
        return 'h-14 text-lg px-5'
      default: // md
        return 'h-12 text-3 px-4'
    }
  }

  // 获取容器类名
  const containerClasses = `relative ${getSizeClasses().split(' ')[0]} ${className}`

  // 获取输入框类名
  const inputClasses = `
    w-full h-full outline-none text-black
    placeholder-[#C9CDD4]
    text-base
    outline-none
    transition-[border-color,border-width] duration-300
    ${getSizeClasses()}
    ${showBorder 
      ? `border border-solid ${borderRadius || 'rounded-2.5'}` 
      : 'border-none'
    }
    ${showBorder && isFocus 
      ? `${borderColor || 'border-[#667085]'} ${borderWidth || 'border-1'}` 
      : showBorder 
        ? `${borderColor || 'border-[#EAECF0]'} ${borderWidth || 'border-1'}`
        : ''
    }
    ${isErrored && showBorder ? 'border-[#ff0000]' : ''}
    ${backgroundColor ? '' : 'bg-transparent'}
    ${isShowSmallEyes ? 'pr-12' : ''}
    ${disabled ? 'cursor-not-allowed opacity-60' : ''}
  `.trim().replace(/\s+/g, ' ')

  // 动态样式对象
  const inputStyle: React.CSSProperties = {
    ...(backgroundColor && { backgroundColor }),
  }

  return (
    <div className={containerClasses}>
      <input
        {...restProps}
        type={isSecureTextEntry ? 'password' : 'text'}
        value={value}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        className={inputClasses}
        style={inputStyle}
        disabled={disabled}
      />
      {isShowSmallEyes && (
        <button
          type="button"
          onClick={handleEyesIconPress}
          className="absolute right-4 top-1/2 transform -translate-y-1/2 w-5 h-5 flex items-center justify-center"
        >
          {isSecureTextEntry ? (
            <img src="/icon/pwd-hide.svg" alt={t('隐藏密码')} />
          ) : (
            <img src="/icon/pwd-show.svg" alt={t('显示密码')} />
          )}
        </button>
      )}
    </div>
  )
})